import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { bindAll } from '@bibliocommons/utils-react';
import { accountShape, branchShape, bibShape, libraryShape } from '@bibliocommons/bc-prop-types';
import { TransactionalSolidButton } from '@bibliocommons/base-buttons';
import {
  FullScreenOverlayBody,
  FullScreenOverlayHeader,
  FullScreenOverlayContent,
  FullScreenOverlayFooter
} from '@bibliocommons/base-full-screen-overlay';
import { TableStencil } from '@bibliocommons/table';

import { BibBriefContainer } from 'app/components/bibs/shared/BibBrief';
import PickupBranchSelect from 'app/components/bibs/shared/PickupBranchSelect';
import ItemLevelHoldsTable from './ItemLevelHoldsTable';

import './ItemLevelHoldsForm.scss';

export default class ItemLevelHoldsForm extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
    this.state = {
      selectedBranchCode: props.preferredBranches.first() ? props.preferredBranches.first().get('code') : '',
      selectedItems: Immutable.Set()
    };
  }

  handleBranchChange(selectedBranchCode) {
    this.setState({ selectedBranchCode });
  }

  select(item) {
    this.setState(({ selectedItems }) => ({
      selectedItems: selectedItems.add(item)
    }));
  }

  unselect(item) {
    this.setState(({ selectedItems }) => ({
      selectedItems: selectedItems.delete(item)
    }));
  }

  handleSingleSelect(item) {
    this.setState({
      selectedItems: Immutable.Set([item.get('itemId')])
    });
  }

  handleBatchSelect(item) {
    if (this.state.selectedItems.includes(item.get('itemId'))) {
      return this.unselect(item.get('itemId'));
    }

    return this.select(item.get('itemId'));
  }

  handleSubmit(e) {
    e.preventDefault();
    this.props.holdActions.placeItemHold({
      metadataId: this.props.metadataId,
      itemIds: this.state.selectedItems.toJS(),
      branchId: this.state.selectedBranchCode,
      postSuccess: this.props.handleClose
    });
  }

  renderBranchSelect() {
    const { currentAccount, branches, preferredBranches } = this.props;

    return (
      <PickupBranchSelect
        branches={branches}
        preferredBranches={preferredBranches}
        currentAccount={currentAccount}
        selectedBranchCode={this.state.selectedBranchCode}
        handleChange={this.handleBranchChange}
      />
    );
  }

  renderConfirmButton() {
    const { metadataId, holdsTransactions } = this.props;
    const isPlacingHold = holdsTransactions.getIn([metadataId, 'isPlacingHold'], false);
    const noItemSelected = !this.state.selectedItems.toJS().some(Boolean);

    return (
      <TransactionalSolidButton
        className="confirm-hold-button"
        dataKey="confirm-hold-button"
        isLoading={isPlacingHold}
        disabled={noItemSelected}
        type="submit"
      >
        <FormattedMessage id="button_confirm_hold" />
      </TransactionalSolidButton>
    );
  }

  render() {
    return (
      <FullScreenOverlayBody className="cp-item-level-holds-form" tagName="form" onSubmit={this.handleSubmit}>
        <FullScreenOverlayHeader title={<FormattedMessage id="place_hold" />} />
        <FullScreenOverlayContent>
          <BibBriefContainer metadataId={this.props.bib.get('id')} />
          {this.renderBranchSelect()}
          {this.props.isFetching ? (
            <TableStencil />
          ) : (
            <ItemLevelHoldsTable
              selectedItems={this.state.selectedItems}
              handleSingleSelect={item => this.handleSingleSelect(item)}
              handleBatchSelect={item => this.handleBatchSelect(item)}
              bib={this.props.bib}
              holdableItems={this.props.holdableItems}
              currentLibrary={this.props.currentLibrary}
              holds={this.props.holds}
            />
          )}
        </FullScreenOverlayContent>
        <FullScreenOverlayFooter renderPrimaryAction={this.renderConfirmButton} />
      </FullScreenOverlayBody>
    );
  }
}

ItemLevelHoldsForm.propTypes = {
  isFetching: PropTypes.bool.isRequired,
  bib: bibShape.isRequired,
  branches: ImmutablePropTypes.listOf(branchShape).isRequired,
  currentAccount: accountShape.isRequired,
  currentLibrary: libraryShape.isRequired,
  handleClose: PropTypes.func.isRequired,
  holdableItems: ImmutablePropTypes.list.isRequired,
  holdActions: PropTypes.objectOf(PropTypes.func).isRequired,
  holds: ImmutablePropTypes.map.isRequired,
  holdsTransactions: ImmutablePropTypes.map.isRequired,
  metadataId: PropTypes.string.isRequired,
  preferredBranches: ImmutablePropTypes.listOf(branchShape).isRequired
};
